import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { packageJson } from '../../../environments/env';
import { buildTime } from '../../build-info';
import { BreadcrumbComponent } from '../../components/utility/breadcrumb/breadcrumb.component';
import {
  ServerSettingsModalComponent,
  ServerSettingsModalData,
  ServerSettingsModalResult,
} from '../../modals/server-settings-modal/server-settings-modal.component';
import { Record, Region, SubRegion } from '../../models/patient-records.model';
import { Settings } from '../../models/settings.model';
import { AlertService } from '../../services/alert.service';
import { Cp2ApiService } from '../../services/cp2-api.service';
import { DataRepositoryService } from '../../services/datarepository.service';
import { AccessFacadeService } from '../../services/facades/access-facade.service';
import { AreaFacadeService } from '../../services/facades/areas-facade.service';
import { RecordsFacadeService } from '../../services/facades/records-facade.service';
import { ServerSettingsFacadeService } from '../../services/facades/server-settings-facade.service';
import { SettingsFacadeService } from '../../services/facades/settings-facade.service';
import { ThemeService } from '../../services/theme.service';

interface LanguageTranslations {
  COMPONENT: {
    SETTINGS: {
      LANGUAGE: {
        german_label: string;
        english_label: string;
      };
    };
  };
}

@Component({
  selector: 'app-setting',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatSelectModule,
    RouterModule,
    TranslateModule,
    MatCheckboxModule,
    MatChipsModule,
    BreadcrumbComponent,
  ],
})
export class SettingsComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['name', 'subAreas', 'validFrom', 'validUntil', 'actions'];
  public languages: Array<{ lang: string; title: string }> = [];
  public settings$ = this.settingsFacade.settings$;
  public selectedColorSchemas: string = '';
  public selectedLanguage: string = '';
  protected readonly toolbar = toolbar;
  private allSubs: Subscription[] = [];
  public subAreas: SubRegion[] = [];
  public activeAnonymous!: boolean;
  public demoMode: boolean = false;
  public records: Record[] = [];
  public buildTime = buildTime;
  public areas: Region[] = [];
  public appVersion = '';

  public colorSchemes: Array<{ color: string; mode: string; label: string }> = [
    { color: 'default', mode: 'light', label: 'Standard' },
    { color: 'rostock', mode: 'light', label: 'Rostock' },
    { color: 'neutral', mode: 'light', label: 'Neutral' },
  ];

  public syncArray: { name: string; selected: boolean }[] = [
    { name: 'ACH Station 11', selected: true },
    { name: 'GAST Station 33', selected: true },
    { name: 'Kardiologie Privat', selected: true },
    { name: 'KARD Station 22', selected: true },
    { name: 'Ambulanz', selected: true },
    { name: 'TX', selected: true },
    { name: 'Prä-OP-Präsentation', selected: false },
    { name: 'Post-OP-Präsentation', selected: false },
    { name: 'Dienstpräsentation', selected: false },
    { name: 'Gespeicherte Patienten', selected: false },
    { name: 'Tagesklinik ITZ Alle', selected: false },
    { name: 'Tagesklinik ITZ Unzugeordnet', selected: false },
    { name: 'Tagesklinik ITZ Meine', selected: false },
    { name: 'Tagesklinik ITZ Zugeordnet', selected: false },
  ];

  public interactionModule: { name: string }[] = [
    { name: 'Neues Interacktionsmodul' },
    { name: 'Registrierter Patient' },
    { name: 'Rektum Ca' },
    { name: 'Shilddrüse' },
    { name: 'Stroke' },
  ];

  public constructor(
    private api: Cp2ApiService,
    private alert: AlertService,
    private areasFacade: AreaFacadeService,
    private accessFacade: AccessFacadeService,
    private readonly dialog: MatDialog,
    private recordsFacade: RecordsFacadeService,
    private repo: DataRepositoryService,
    private serverSettingsFacade: ServerSettingsFacadeService,
    private settingsFacade: SettingsFacadeService,
    private themeService: ThemeService,
    private translate: TranslateService,
  ) {
    this.appVersion = packageJson.version;

    this.areasFacade.loadAreas();
    this.areasFacade.loadSubAreas();
    this.recordsFacade.loadSelectedRecord();
    this.recordsFacade.loadRecords();
  }

  public async ngOnInit(): Promise<void> {
    this.loadInitialSettings();
  }

  public ngOnDestroy(): void {
    this.allSubs.forEach((s: Subscription) => {
      s.unsubscribe();
    });
    this.allSubs = [];
  }

  public onSettingLangChange($event: any): void {
    const sub: Subscription = this.settingsFacade.settings$
      .pipe(
        take(1),
        tap(currentSettings => {
          const updatedSettings = {
            ...currentSettings,
            language: $event.options[0].value,
          };
          this.settingsFacade.saveSettings(updatedSettings);
        }),
      )
      .subscribe();
    this.allSubs.push(sub);
  }

  public onSyncItemChange($event: boolean, item: { name: string; selected: boolean }) {
    item.selected = $event;
  }

  public async onResetDbAndRequestLocators(): Promise<void> {
    const res = confirm(this.translate.instant('COMPONENT.SETTINGS.confirm_reset_local_db'));

    if (res) {
      const token = (await firstValueFrom(this.accessFacade.userTokens$)).token;
      if (!token) {
        console.error('No token found');
        return;
      }

      const device = await this.repo.getDevice();
      const device_uuid = device.deviceId;
      const user_uuid = token.related_user_id;

      let alertText = this.translate.instant('COMPONENT.SETTINGS.reset_local_db_success');

      try {
        await this.api.requestLocatorsResend({ device_uuid, user_uuid }, token.access_token);
        await this.repo.deleteDatabase();
        await this.alert.show({ message: alertText, buttons: [{ role: 'cancel', text: 'OK' }] });
        location.reload();
      } catch (error) {
        alertText = this.translate.instant('COMPONENT.SETTINGS.reset_local_db_failed');
        await this.alert.show({ message: alertText, buttons: [{ role: 'cancel', text: 'OK' }] });
      }
    }
  }

  public onSimulateOfflineChange($event: boolean) {
    this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
      const newSettings: Settings = { ...s, simulateOffline: $event };
      this.settingsFacade.saveSettings(newSettings);
    });
  }

  public toggleDemoMode(demoMode: boolean): void {
    this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
      const newSettings: Settings = { ...s, demoMode };
      this.settingsFacade.saveSettings(newSettings);
    });
  }

  public toggleAnonymization(activeAnonymous: boolean) {
    const sub = this.settingsFacade.settings$
      .pipe(
        take(1),
        tap(currentSettings => {
          const updatedSettings = {
            ...currentSettings,
            activeAnonymous,
          };
          this.settingsFacade.saveSettings(updatedSettings);
        }),
      )
      .subscribe();
    this.allSubs.push(sub);
  }

  public changeColorScheme(colorScheme: any) {
    this.themeService.changeColorScheme(colorScheme.source._value?.toString());

    const sub: Subscription = this.settingsFacade.settings$
      .pipe(
        take(1), // Takes the first emission from settings$ and automatically unsubscribes
        tap(currentSettings => {
          const updatedSettings = {
            ...currentSettings,
            colorScheme: colorScheme.source._value?.toString(),
          };
          this.settingsFacade.saveSettings(updatedSettings);
        }),
      )
      .subscribe();
    this.allSubs.push(sub);
  }

  public async onClickOnServerSetup(): Promise<void> {
    const serverSettings = this.serverSettingsFacade.snapshot;
    const dialogConfig: MatDialogConfig<ServerSettingsModalData> = { width: '80%' };
    if (serverSettings) dialogConfig.data = { serverSettings };
    const dialogRef = this.dialog.open<
      ServerSettingsModalComponent,
      ServerSettingsModalData,
      ServerSettingsModalResult
    >(ServerSettingsModalComponent, dialogConfig);

    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res?.role === 'cancel' || !res?.serverSettings) return;

    this.serverSettingsFacade.saveServerSettings(res.serverSettings);
  }

  private loadInitialSettings(): void {
    const sub: Subscription = this.settingsFacade.settings$.subscribe((settings: Settings) => {
      this.selectedLanguage = settings.language;
      this.activeAnonymous = settings.activeAnonymous;
      this.selectedColorSchemas = settings.colorScheme;
      this.demoMode = settings.demoMode;
      this.translate
        .getTranslation(this.selectedLanguage)
        .subscribe((translations: LanguageTranslations) => {
          this.languages = [
            {
              lang: 'de',
              title: translations.COMPONENT.SETTINGS.LANGUAGE.german_label,
            },
            {
              lang: 'en',
              title: translations.COMPONENT.SETTINGS.LANGUAGE.english_label,
            },
          ];
        });
    });
    this.allSubs.push(sub);
  }
  public onAdminModeChange($event: boolean) {
    this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
      const newSettings: Settings = { ...s, adminMode: $event };
      this.settingsFacade.saveSettings(newSettings);
    });
  }
}
